/**
 * Nutra Gold theme — Favorites (wishlist) behaviour.
 *
 * Persists favorited products in localStorage as `{ id, handle }` entries and
 * powers two surfaces:
 *
 * - <favorites-toggle> buttons rendered by product-card-item.liquid
 * - #favorites-drawer
 *
 * Every change dispatches FAVORITES_CHANGE_EVENT on window so all toggles and
 * the drawer stay synchronized.
 */

/** @constant {string} localStorage key holding the favorited products. */
const STORAGE_KEY = "nutra-gold:favorites";

/** @typedef {{id: string, handle: string}} FavoriteEntry */

/**
 * @typedef {object} Product
 * @property {string} [title]
 * @property {string} [featured_image]
 * @property {number} [price]
 */

/**
 * @typedef {object} ThemeTranslations
 * @property {string} [favorites_add]
 * @property {string} [favorites_remove]
 */

/**
 * @typedef {object} ShopifyRoutes
 * @property {string} [root]
 */

/**
 * @typedef {object} ShopifyCurrency
 * @property {string} [active]
 */

/**
 * @typedef {object} ShopifyGlobal
 * @property {ShopifyRoutes} [routes]
 * @property {ShopifyCurrency} [currency]
 */

/**
 * @typedef {object} ThemeGlobal
 * @property {ThemeTranslations} [translations]
 */

/**
 * @typedef {Event & {target: Element|null}} SectionEvent
 */

/** @constant {string} Event name dispatched on window when favorites change. */
export const FAVORITES_CHANGE_EVENT = "nutra-gold:favorites:change";

/**
 * Safely dispatches the favorites change event.
 *
 * @param {string|null} productId
 * @param {boolean} added
 */
/** @param {string|null} productId @param {boolean} added */
function dispatchFavoritesChange(productId, added) {
  window.dispatchEvent(
    new CustomEvent(FAVORITES_CHANGE_EVENT, {
      detail: {
        productId,
        added,
      },
    }),
  );
}

/**
 * Store for favorite products.
 */
class FavoritesStore {
  /**
   * @returns {FavoriteEntry[]}
   */
  static get() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .filter(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            entry.id != null &&
            entry.handle != null &&
            String(entry.id).trim() !== "" &&
            String(entry.handle).trim() !== "",
        )
        .map((entry) => ({
          id: String(entry.id),
          handle: String(entry.handle),
        }));
    } catch {
      return [];
    }
  }

  /**
   * @param {string|null} productId
   * @returns {boolean}
   */
  static has(productId) {
    if (productId == null || String(productId).trim() === "") {
      return false;
    }

    const normalizedId = String(productId);

    return this.get().some((entry) => String(entry.id) === normalizedId);
  }

  /**
   * Adds a product to favorites.
   *
   * @param {string|null} productId
   * @param {string|null} handle
   * @returns {boolean}
   */
  static add(productId, handle) {
    if (
      productId == null ||
      handle == null ||
      String(productId).trim() === "" ||
      String(handle).trim() === ""
    ) {
      return false;
    }

    const normalizedId = String(productId);
    const normalizedHandle = String(handle);

    const entries = this.get();

    if (entries.some((entry) => String(entry.id) === normalizedId)) {
      return false;
    }

    entries.push({
      id: normalizedId,
      handle: normalizedHandle,
    });

    return this.#save(entries);
  }

  /**
   * Removes a product from favorites.
   *
   * @param {string|null} productId
   * @returns {boolean}
   */
  static remove(productId) {
    if (productId == null || String(productId).trim() === "") {
      return false;
    }

    const normalizedId = String(productId);
    const entries = this.get();

    const filtered = entries.filter(
      (entry) => String(entry.id) !== normalizedId,
    );

    if (filtered.length === entries.length) {
      return false;
    }

    return this.#save(filtered);
  }

  /**
   * Toggles a product in the favorites list.
   *
   * @param {string|null} productId
   * @param {string|null} handle
   * @returns {boolean|null}
   *   true  = added
   *   false = removed
   *   null  = invalid operation
   */
  static toggle(productId, handle) {
    if (productId == null || String(productId).trim() === "") {
      return null;
    }

    if (this.has(productId)) {
      return this.remove(productId) ? false : null;
    }

    return this.add(productId, handle) ? true : null;
  }

  /**
   * @param {FavoriteEntry[]} entries
   * @returns {boolean}
   */
  static #save(entries) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Renders the favorite toggle of a product card.
 *
 * @extends {HTMLElement}
 */
class FavoritesToggle extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {boolean} */
  #connected = false;

  connectedCallback() {
    this.#button = this.querySelector("button");

    if (!this.#button) {
      return;
    }

    if (this.#connected) {
      this.#sync();
      return;
    }

    this.#connected = true;

    this.#sync();

    this.#button.addEventListener("click", this.#handleClick);
    window.addEventListener(FAVORITES_CHANGE_EVENT, this.#handleExternalChange);
    window.addEventListener("storage", this.#handleExternalChange);
  }

  disconnectedCallback() {
    if (!this.#connected || !this.#button) {
      return;
    }

    this.#button.removeEventListener("click", this.#handleClick);

    window.removeEventListener(
      FAVORITES_CHANGE_EVENT,
      this.#handleExternalChange,
    );

    window.removeEventListener("storage", this.#handleExternalChange);

    this.#connected = false;
    this.#button = null;
  }

  /**
   * @returns {string|null}
   */
  get #productId() {
    return this.getAttribute("data-product-id");
  }

  /**
   * @returns {string|null}
   */
  get #productHandle() {
    return this.getAttribute("data-product-handle");
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const productId = this.#productId;
    const handle = this.#productHandle;

    if (!productId || !handle) {
      console.warn("[Nutra Favorites] Missing product ID or handle.", {
        productId,
        handle,
      });
      return;
    }

    const added = FavoritesStore.toggle(productId, handle);

    if (added === null) {
      return;
    }

    dispatchFavoritesChange(productId, added);
  };

  #handleExternalChange = () => {
    this.#sync();
  };

  #sync() {
    if (!this.#button) {
      return;
    }

    const productId = this.#productId;
    const favorited = FavoritesStore.has(productId);

    const translations = /** @type {any} */ (window).Theme?.translations || {};

    this.#button.setAttribute("aria-pressed", String(favorited));

    this.#button.setAttribute(
      "aria-label",
      favorited
        ? translations.favorites_remove || "Remove from favorites"
        : translations.favorites_add || "Add to favorites",
    );

    this.#button.classList.toggle("is-favorited", favorited);

    this.#button.dataset.favorited = String(favorited);
  }
}

if (!customElements.get("favorites-toggle")) {
  customElements.define("favorites-toggle", FavoritesToggle);
}

/**
 * Controls the favorites drawer.
 */
class FavoritesDrawerController {
  /** @type {HTMLElement|null} */
  root = null;

  /** @type {HTMLElement|null} */
  bodyElement = null;

  /** @type {HTMLElement|null} */
  emptyElement = null;

  /** @type {HTMLElement|null} */
  itemsElement = null;

  /** @type {number} */
  renderToken = 0;

  /** @type {boolean} */
  initialized = false;

  constructor() {
    this.attach();

    document.addEventListener(
      "shopify:section:load",
      /** @type {EventListener} */ (this.#handleSectionLoad),
    );

    document.addEventListener(
      "shopify:section:unload",
      /** @type {EventListener} */ (this.#handleSectionUnload),
    );

    window.addEventListener(
      FAVORITES_CHANGE_EVENT,
      this.#handleFavoritesChange,
    );

    window.addEventListener("storage", this.#handleFavoritesChange);
  }

  /**
   * Reconnects the controller to the current drawer.
   */
  attach() {
    const root = document.getElementById("favorites-drawer");

    if (!root) {
      this.root = null;
      this.bodyElement = null;
      this.emptyElement = null;
      this.itemsElement = null;
      return;
    }

    const changed = this.root !== root;

    this.root = root;

    this.bodyElement = root.querySelector(".favorites-drawer__body");

    this.emptyElement = root.querySelector(".favorites-drawer__empty");

    this.itemsElement = root.querySelector(".favorites-drawer__items");

    if (changed || !this.initialized) {
      this.#bindOpenTriggers();
      this.initialized = true;
    }

    this.render();
  }

  /**
   * Binds drawer open triggers without creating duplicate listeners.
   */
  #bindOpenTriggers() {
    document
      .querySelectorAll('[data-drawer-open="favorites-drawer"]')
      .forEach((trigger) => {
        if (!(trigger instanceof HTMLElement)) {
          return;
        }

        if (trigger.dataset.favoritesBound === "true") {
          return;
        }

        trigger.dataset.favoritesBound = "true";

        trigger.addEventListener("click", () => {
          this.render();
        });
      });
  }

  /** @param {SectionEvent} event */
  #handleSectionLoad = (event) => {
    if (
      event.target?.querySelector?.("#favorites-drawer") ||
      event.target?.matches?.("#favorites-drawer")
    ) {
      this.attach();
    }

    this.#bindOpenTriggers();
  };

  /** @param {SectionEvent} event */
  #handleSectionUnload = (event) => {
    if (
      event.target?.querySelector?.("#favorites-drawer") ||
      event.target?.matches?.("#favorites-drawer")
    ) {
      this.renderToken++;
      this.root = null;
      this.bodyElement = null;
      this.emptyElement = null;
      this.itemsElement = null;
    }
  };

  #handleFavoritesChange = () => {
    this.attach();
  };

  /**
   * Renders the favorites drawer.
   * @returns {Promise<void>}
   */
  async render() {
    if (
      !this.root ||
      !this.bodyElement ||
      !this.emptyElement ||
      !this.itemsElement
    ) {
      return;
    }

    const entries = FavoritesStore.get();
    const token = ++this.renderToken;

    if (!entries.length) {
      this.emptyElement.hidden = false;
      this.itemsElement.hidden = true;
      this.itemsElement.innerHTML = "";
      return;
    }

    this.emptyElement.hidden = true;
    this.itemsElement.hidden = false;

    const rootUrl =
      /** @type {ShopifyGlobal | undefined} */ (window.Shopify)?.routes?.root ||
      "/";

    const products = await Promise.all(
      entries.map(async ({ handle }) => {
        const encodedHandle = encodeURIComponent(handle);

        try {
          const response = await fetch(
            `${rootUrl}products/${encodedHandle}.js`,
            {
              headers: {
                Accept: "application/json",
              },
            },
          );

          if (!response.ok) {
            return null;
          }

          return await response.json();
        } catch {
          return null;
        }
      }),
    );

    // Another render started while the requests were pending.
    if (token !== this.renderToken) {
      return;
    }

    // The favorites may have changed while fetching.
    const currentEntries = FavoritesStore.get();

    if (
      currentEntries.length !== entries.length ||
      currentEntries.some(
        (entry, index) =>
          entry.id !== entries[index]?.id ||
          entry.handle !== entries[index]?.handle,
      )
    ) {
      return this.render();
    }

    this.itemsElement.innerHTML = "";

    entries.forEach((entry, index) => {
      const item = this.#buildItem(products[index], entry);

      if (item && this.itemsElement) {
        this.itemsElement.appendChild(item);
      }
    });
  }

  /**
   * @param {Product|null} product
   * @param {FavoriteEntry} entry
   * @returns {HTMLDivElement}
   */
  #buildItem(product, entry) {
    const item = document.createElement("div");
    item.className = "favorites-drawer__item";

    /*
     * Image
     */
    if (product?.featured_image) {
      const image = document.createElement("img");

      image.className = "favorites-drawer__item-image";

      image.loading = "lazy";
      image.width = 80;
      image.height = 80;
      image.src = product.featured_image;
      image.alt = product.title || entry.handle;

      item.appendChild(image);
    }

    /*
     * Details
     */
    const details = document.createElement("div");

    details.className = "favorites-drawer__item-details";

    item.appendChild(details);

    /*
     * Product title
     */
    const title = document.createElement("a");

    title.className = "favorites-drawer__item-title";

    const rootUrl =
      /** @type {ShopifyGlobal | undefined} */ (window.Shopify)?.routes?.root ||
      "/";

    const encodedHandle = encodeURIComponent(entry.handle);

    title.href = `${rootUrl}products/${encodedHandle}`;

    title.textContent = product?.title || entry.handle;

    details.appendChild(title);

    /*
     * Price
     */
    const price = document.createElement("p");

    price.className = "favorites-drawer__item-price";

    if (typeof product?.price === "number") {
      price.textContent = this.#formatPrice(product.price);
    }

    details.appendChild(price);

    /*
     * Remove button
     */
    const remove = document.createElement("button");

    remove.type = "button";

    remove.className = "favorites-drawer__item-remove";

    remove.dataset.productId = entry.id;

    const translations =
      /** @type {{ Theme?: { translations?: Record<string, string> } }} */ (
        window
      ).Theme?.translations || {};

    remove.textContent = translations.favorites_remove || "Remover";

    remove.setAttribute(
      "aria-label",
      `${
        translations.favorites_remove || "Remover"
      } ${product?.title || entry.handle}`,
    );

    remove.addEventListener("click", () => {
      const removed = FavoritesStore.remove(entry.id);

      if (!removed) {
        return;
      }

      dispatchFavoritesChange(entry.id, false);
    });

    details.appendChild(remove);

    return item;
  }

  /**
   * Formats a cent-amount price using the active shop currency.
   *
   * @param {number} cents
   * @returns {string}
   */
  #formatPrice(cents) {
    const currency = window.Shopify?.currency?.active || "BRL";

    try {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
      }).format(cents / 100);
    } catch {
      return `R$ ${(cents / 100).toFixed(2).replace(".", ",")}`;
    }
  }
}

/**
 * Initialize only once.
 */
/** @type {{ __nutraFavoritesDrawer?: FavoritesDrawerController }} */
const favoritesWindow = /** @type {*} */ (window);

if (!favoritesWindow.__nutraFavoritesDrawer) {
  favoritesWindow.__nutraFavoritesDrawer = new FavoritesDrawerController();
}
